<template>
  <section v-show="active" class="app-modal__section">
    <header class="app-modal__section-head">
      <h4>{{ text.title }}</h4>
      <p>{{ text.subtitle }}</p>
    </header>

    <div class="app-modal__grid two-columns">
      <div v-if="showInvoiceFiles" class="app-modal-field full-width">
        <span>{{ text.invoiceFiles }}</span>
        <div class="expense-files-grid">
          <div class="expense-file-upload">
            <input
              ref="invoicePdfInput"
              type="file"
              class="expense-file-upload__input"
              accept="application/pdf"
              @change="onFileChange('invoicePdfFile', $event)"
            />
            <button
              type="button"
              class="app-header-btn app-header-btn--secondary expense-file-upload__trigger"
              @click="openFilePicker('invoicePdfInput')"
            >
              PDF
            </button>
            <span class="expense-file-upload__name">{{ resolveFileName(form.invoicePdfFile) }}</span>
          </div>

          <div class="expense-file-upload">
            <input
              ref="invoiceXmlInput"
              type="file"
              class="expense-file-upload__input"
              accept="text/xml,application/xml,.xml"
              @change="onFileChange('invoiceXmlFile', $event)"
            />
            <button
              type="button"
              class="app-header-btn app-header-btn--secondary expense-file-upload__trigger"
              @click="openFilePicker('invoiceXmlInput')"
            >
              XML
            </button>
            <span class="expense-file-upload__name">{{ resolveFileName(form.invoiceXmlFile) }}</span>
          </div>
        </div>
      </div>

      <div class="app-modal-field" :class="{ 'full-width': !showPurchaseOrder }">
        <span>{{ text.voucher }}</span>
        <div class="expense-file-upload">
          <input
            ref="voucherFileInput"
            type="file"
            class="expense-file-upload__input"
            @change="onFileChange('voucherFile', $event)"
          />
          <button
            type="button"
            class="app-header-btn app-header-btn--secondary expense-file-upload__trigger"
            @click="openFilePicker('voucherFileInput')"
          >
            {{ text.selectFile }}
          </button>
          <span class="expense-file-upload__name">{{ resolveFileName(form.voucherFile) }}</span>
        </div>
      </div>

      <div v-if="showPurchaseOrder" class="app-modal-field">
        <span>{{ text.purchaseOrder }}</span>
        <div class="expense-file-upload">
          <input
            ref="purchaseOrderFileInput"
            type="file"
            class="expense-file-upload__input"
            @change="onFileChange('purchaseOrderFile', $event)"
          />
          <button
            type="button"
            class="app-header-btn app-header-btn--secondary expense-file-upload__trigger"
            @click="openFilePicker('purchaseOrderFileInput')"
          >
            {{ text.selectFile }}
          </button>
          <span class="expense-file-upload__name">{{ resolveFileName(form.purchaseOrderFile) }}</span>
        </div>
      </div>

      <div class="app-modal-field full-width">
        <span>{{ text.additionalFile }}</span>

        <div class="expense-additional-mode">
          <label class="app-modal-toggle">
            <input
              type="checkbox"
              :checked="additionalFileMode === 'file'"
              @change="setAdditionalFileMode('file')"
            />
            <span>{{ text.additionalModeFile }}</span>
          </label>

          <label class="app-modal-toggle">
            <input
              type="checkbox"
              :checked="additionalFileMode === 'url'"
              @change="setAdditionalFileMode('url')"
            />
            <span>{{ text.additionalModeUrl }}</span>
          </label>
        </div>

        <div v-if="additionalFileMode === 'file'" class="expense-file-upload">
          <input
            ref="additionalFileInput"
            type="file"
            class="expense-file-upload__input"
            @change="onFileChange('additionalFile', $event)"
          />
          <button
            type="button"
            class="app-header-btn app-header-btn--secondary expense-file-upload__trigger"
            @click="openFilePicker('additionalFileInput')"
          >
            {{ text.selectFile }}
          </button>
          <span class="expense-file-upload__name">{{ resolveFileName(form.additionalFile) }}</span>
        </div>

        <input
          v-else
          v-model.trim="form.additionalFileUrl"
          type="url"
          class="form-control"
          :placeholder="text.placeholders.additionalUrl"
        />
      </div>
    </div>
  </section>
</template>

<script>
export default {
  name: 'ExpenseFeeModalSectionFiles',
  props: {
    active: {
      type: Boolean,
      default: false,
    },
    form: {
      type: Object,
      required: true,
    },
    entityLabel: {
      type: String,
      default: 'gasto',
    },
    showPurchaseOrder: {
      type: Boolean,
      default: true,
    },
    text: {
      type: Object,
      required: true,
    },
  },
  computed: {
    additionalFileMode() {
      const mode = String(this.form?.additionalFileMode || '').trim().toLowerCase();
      return mode === 'url' ? 'url' : 'file';
    },
    voucherType() {
      return String(this.form?.voucherType || '').trim().toLowerCase();
    },
    showInvoiceFiles() {
      return this.voucherType === 'cfdi';
    },
  },
  watch: {
    voucherType: {
      immediate: true,
      handler(nextType) {
        const normalized = String(nextType || '').trim().toLowerCase();
        if (normalized === 'cfdi') {
          return;
        }

        this.form.invoicePdfFile = null;
        this.form.invoiceXmlFile = null;
      },
    },
  },
  methods: {
    openFilePicker(refName) {
      const input = this.$refs[refName];
      if (input && typeof input.click === 'function') {
        input.click();
      }
    },
    onFileChange(field, event) {
      const file = event?.target?.files?.[0] || null;
      this.form[field] = file;

      if (field === 'additionalFile') {
        this.form.additionalFileMode = 'file';
      }
    },
    setAdditionalFileMode(mode) {
      const normalized = String(mode || '').trim().toLowerCase();
      if (normalized === 'url') {
        this.form.additionalFileMode = 'url';
        this.form.additionalFile = null;
        return;
      }

      if (normalized === 'file') {
        this.form.additionalFileMode = 'file';
        this.form.additionalFileUrl = '';
      }
    },
    resolveFileName(file) {
      return file?.name || this.text.noFile;
    },
  },
};
</script>

<style scoped>
.expense-files-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 10px;
}

.expense-file-upload {
  border: 1px solid rgba(153, 152, 153, 0.3);
  border-radius: 8px;
  min-height: 44px;
  padding: 6px 10px;
  display: flex;
  align-items: center;
  gap: 10px;
  background: #ffffff;
}

.expense-file-upload__input {
  display: none;
}

.expense-file-upload__trigger {
  min-height: 32px;
  padding: 0 12px;
  border-radius: 6px;
  font-size: 12px;
  flex-shrink: 0;
}

.expense-file-upload__name {
  display: inline-flex;
  align-items: center;
  min-height: 32px;
  font-size: 12px;
  color: #4b5563;
  margin: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.expense-additional-mode {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 10px;
  margin-bottom: 8px;
  font-size: 13px;
  color: #4b5563;
}

.expense-additional-mode label {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  line-height: 1;
  min-height: 40px;
  width: 100%;
  border: 1px solid rgba(153, 152, 153, 0.3);
  border-radius: 8px;
  padding: 0 12px;
  background: #ffffff;
}

.expense-additional-mode label input[type='checkbox'] {
  margin: 0;
}

@media (max-width: 760px) {
  .expense-files-grid {
    grid-template-columns: 1fr;
  }
}
</style>
